import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ClockIcon,
  ExternalLinkIcon,
  MapPinIcon,
  PlayIcon,
  VideoIcon,
} from 'lucide-react';
import type {
  AgendaEvent,
  WebinarRecording,
  CourseAudience,
  ProgressStatus,
  ExamQuestionClient,
  ScorePerModule,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { apiGet, apiGetBlob } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { StudentCourseOverview } from '../components/elearning/StudentCourseOverview.js';
import { StudentCourseDetail } from '../components/elearning/StudentCourseDetail.js';
import { StudentLessonViewer } from '../components/elearning/StudentLessonViewer.js';
import { StudentExam } from '../components/elearning/StudentExam.js';
import { StudentPractice } from '../components/elearning/StudentPractice.js';
import { HupperColleges } from '../components/elearning/HupperColleges.js';

// ─── API ─────────────────────────────────────────────────────────────────────
// Alle cursistenroutes hangen onder deze basis; 404 als ELEARNING_ENABLED uit
// staat (backend). De cursistenkant leest en schrijft uitsluitend hier.
export const ELEARNING_BASE = '/api/v1/elearning';

/** Haalt het certificaat op en start de browser-download. Gooit ApiError. */
export async function downloadCertificaat(courseId: string): Promise<void> {
  const { blob, filename } = await apiGetBlob(`${ELEARNING_BASE}/courses/${courseId}/certificate`);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename ?? 'Certificaat.pdf';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Response-vormen (het REST-contract, niet de Firestore-schemas) ──────────
// De backend levert afgeleide velden (voortgangspercentage, hasExam, …) die
// niet 1-op-1 in de shared Zod-schemas staan; daarom hier als losse interfaces.
// Enums en de client-veilige examenvraag komen wél uit @kennisbank/shared.

export interface CourseListItem {
  id: string;
  name: string;
  description: string;
  audience: CourseAudience;
  /** Voortgangsstatus van de ingelogde gebruiker voor deze cursus. */
  status: ProgressStatus;
  progressPercentage: number;
  totalLessons: number;
  completedLessons: number;
  hasExam: boolean;
  videoQuestionsTotal: number;
  videoQuestionsAnswered: number;
  /** Eén-lescursus: open deze les direct, zonder cursuspagina ertussen. */
  directLessonId: string | null;
}

export interface ProgressView {
  userId: string;
  courseId: string;
  status: ProgressStatus;
  completedLessonIds: string[];
  totalLessons: number;
  completedLessons: number;
  progressPercentage: number;
  examAttemptsCount: number;
  bestExamScore: number | null;
  attemptsRemaining: number;
  hasExam: boolean;
  canStartExam: boolean;
  videoQuestionsTotal: number;
  videoQuestionsAnswered: number;
  videoAnswers: Record<string, number>;
}

export interface CourseDetailCourse {
  id: string;
  name: string;
  description: string;
  audience: CourseAudience;
  passPercentage: number;
  maxRetakes: number;
  cooldownMinutes: number;
  certificateEnabled: boolean;
}

export interface CourseDetailLesson {
  id: string;
  name: string;
  content: string;
  sortOrder: number;
  hasVideo: boolean;
}

export interface CourseDetailModule {
  id: string;
  name: string;
  sortOrder: number;
  lessons: CourseDetailLesson[];
  practiceCount: number;
  examCount: number;
  /** Vragen die de videospeler op hun tijdstip toont (op tijd gesorteerd). */
  videoQuestions: VideoQuestion[];
}

export interface CourseDetailResponse {
  course: CourseDetailCourse;
  modules: CourseDetailModule[];
  progress: ProgressView;
}

export interface StartExamResponse {
  attemptId: string;
  attemptNumber: number;
  questions: ExamQuestionClient[];
}

export interface SubmitExamResponse {
  passed: boolean;
  scorePercentage: number;
  scorePerModule: ScorePerModule[];
  status: ProgressStatus;
  attemptsRemaining: number;
  cooldownUntil: string | null;
  courseAverageScore: number | null;
}

export interface PracticeAnswerOption {
  text: string;
  isCorrect: boolean;
  sortOrder: number;
}

export interface PracticeQuestion {
  id: string;
  moduleId: string;
  title: string;
  questionText: string;
  explanation: string;
  points: number;
  answerOptions: PracticeAnswerOption[];
}

export interface VideoQuestion extends PracticeQuestion {
  videoSeconds: number;
}

export interface PracticeResponse {
  questions: PracticeQuestion[];
}

// ─── Webinars: leesprojectie op de agenda ────────────────────────────────────
// Besluit 30 juli 2026: geen eigen datamodel. De tab toont komende agenda-items
// van type `nascholing` met een Teams-link; aanmelden en ICS-download blijven op
// /agenda, zodat er niets dubbel wordt bijgehouden.

/** Vandaag in NL-tijd als YYYY-MM-DD, zodat de vergelijking met `date` een
 *  pure stringvergelijking is en niet meeschuift met de browser-tijdzone. */
function vandaagNL(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Amsterdam' }).format(new Date());
}

/** "wo 12 augustus 2026". Middag-UTC als ankerpunt: dat valt in elke tijdzone
 *  op dezelfde NL-kalenderdag, dus de datum kan nooit een dag verspringen. */
function datumNL(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Intl.DateTimeFormat('nl-NL', {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Europe/Amsterdam',
  }).format(new Date(Date.UTC(year!, month! - 1, day!, 12)));
}

export function komendeWebinars(events: AgendaEvent[], vandaag: string): AgendaEvent[] {
  return events
    .filter(
      (e) =>
        e.evenementtype === 'nascholing' &&
        (!!e.teamsUrl || e.teamsUrlNaAanmelding === true) &&
        e.date >= vandaag,
    )
    .sort((a, b) => a.date.localeCompare(b.date));
}

function WebinarCard({ event }: { event: AgendaEvent }): JSX.Element {
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-brand-100 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-2.5">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
          <VideoIcon size={18} />
        </span>
        <div className="min-w-0">
          <h2 className="line-clamp-2 text-[15px] font-bold leading-snug text-ink-900">
            {event.title}
          </h2>
          <div className="mt-1 flex items-center gap-1.5 text-[12px] text-ink-900/55">
            <CalendarDaysIcon size={13} className="shrink-0" />
            <span>{datumNL(event.date)}</span>
          </div>
          {event.time && (
            <div className="mt-1 flex items-center gap-1.5 text-[12px] text-ink-900/55">
              <ClockIcon size={13} className="shrink-0" />
              <span>{event.time}</span>
            </div>
          )}
          {event.location && (
            <div className="mt-1 flex items-center gap-1.5 text-[12px] text-ink-900/55">
              <MapPinIcon size={13} className="shrink-0" />
              <span className="truncate">{event.location}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 pt-1">
        {event.teamsUrl ? (
          <a
            href={event.teamsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-600"
          >
            Deelnemen via Teams
            <ExternalLinkIcon size={14} strokeWidth={2} />
          </a>
        ) : (
          <Link
            to={`/agenda?item=${event.id}`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-500 px-3 py-1.5 text-[13px] font-semibold text-white transition-colors hover:bg-brand-600"
          >
            Meld je aan om de link te zien
          </Link>
        )}
        <Link to="/agenda" className="text-[12px] font-medium text-petrol-500 hover:underline">
          Bekijk in agenda
        </Link>
      </div>
    </div>
  );
}

/** Opname zoals de lijst hem levert: het objectpad blijft op de backend. */
type WebinarListItem = Omit<WebinarRecording, 'videoPath'>;

/** Opname-kaart: de signed URL wordt pas bij "Afspelen" gemunt (4 uur geldig). */
function WebinarOpnameCard({ webinar }: { webinar: WebinarListItem }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  function play(): void {
    setFailed(false);
    void apiGet<{ url: string }>(`${ELEARNING_BASE}/webinars/${webinar.id}/video-url`)
      .then((r) => setUrl(r.url))
      .catch(() => setFailed(true));
  }

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-brand-100 bg-white p-5 shadow-sm">
      {url ? (
        <video
          controls
          playsInline
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          autoPlay
          preload="metadata"
          src={url}
          className="aspect-video w-full rounded-xl bg-black"
        />
      ) : (
        <button
          type="button"
          onClick={play}
          aria-label={`Speel ${webinar.title} af`}
          className="flex aspect-video w-full items-center justify-center rounded-xl bg-brand-50 text-brand-600 transition-colors hover:bg-brand-100"
        >
          <PlayIcon size={36} />
        </button>
      )}
      <div>
        <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-ink-900">
          {webinar.title}
        </h3>
        {webinar.date && (
          <div className="mt-1 flex items-center gap-1.5 text-[12px] text-ink-900/55">
            <CalendarDaysIcon size={13} className="shrink-0" />
            <span>{datumNL(webinar.date)}</span>
          </div>
        )}
        {failed && (
          <p className="mt-1 text-[12px] text-error">Kon de opname niet laden. Probeer opnieuw.</p>
        )}
      </div>
    </div>
  );
}

function WebinarOpnames(): JSX.Element | null {
  const { user } = useAuth();
  const isInternal = user?.role === 'suppohrt_admin' || user?.role === 'suppohrt_medewerker';
  const [webinars, setWebinars] = useState<WebinarListItem[]>([]);

  useEffect(() => {
    // Externen niet laten aankloppen: de route is requireInternal en elke 403
    // schrijft een access-refused-regel in het beveiligingslog.
    if (!isInternal) return;
    // ponytail: stil falen; zonder opnames toont de sectie gewoon niets extra
    void apiGet<{ webinars: WebinarListItem[] }>(`${ELEARNING_BASE}/webinars`)
      .then((d) => setWebinars(d.webinars))
      .catch(() => {});
  }, [isInternal]);

  if (webinars.length === 0) return null;
  return (
    <div className="mt-6">
      <h3 className="mb-3 text-[15px] font-bold text-ink-900">Terugkijken</h3>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {webinars.map((w) => (
          <WebinarOpnameCard key={w.id} webinar={w} />
        ))}
      </div>
    </div>
  );
}

function WebinarsSection(): JSX.Element {
  const [events, setEvents] = useState<AgendaEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    void apiGet<{ events: AgendaEvent[] }>('/api/v1/agenda')
      .then((d) => setEvents(d.events))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  const webinars = komendeWebinars(events, vandaagNL());

  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-[17px] font-bold text-ink-900">
        <VideoIcon size={18} className="text-brand-600" />
        Webinars
      </h2>
      {loading ? (
        <div className="py-10 text-center text-[13px] text-ink-900/40">Laden…</div>
      ) : loadError ? (
        <div className="rounded-2xl border border-dashed border-brand-200 py-10 text-center">
          <p className="text-[14px] font-medium text-error">
            Kon de webinars niet laden. Ververs de pagina om het opnieuw te proberen.
          </p>
        </div>
      ) : webinars.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-200 py-10 text-center">
          <VideoIcon size={28} className="mx-auto mb-3 text-ink-900/20" />
          <p className="text-[14px] font-medium text-ink-900/50">
            Er zijn op dit moment geen webinars gepland.
          </p>
          <p className="mx-auto mt-1 max-w-md text-[13px] text-ink-900/40">
            Zodra er een webinar in de agenda staat, vind je hier de deelnamelink.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {webinars.map((event) => (
            <WebinarCard key={event.id} event={event} />
          ))}
        </div>
      )}
      <WebinarOpnames />
    </section>
  );
}

// ─── Interne view-navigatie (geen router-routes; alles binnen deze pagina) ───

type View =
  | { name: 'overview' }
  | { name: 'course'; courseId: string }
  // direct: geopend vanuit het overzicht (eén-lescursus), terug = overzicht.
  | { name: 'lesson'; courseId: string; lessonId: string; direct?: boolean }
  | { name: 'exam'; courseId: string }
  | { name: 'practice'; courseId: string; moduleId: string };

/**
 * Academy voor een organisatie zonder eigen leermodules (RHOGO, ADR 0018):
 * webinars uit de eigen agenda plus de externe Hupper-colleges. Raakt de
 * Academy-API niet, die blijft voor deze organisatie dicht.
 */
export function AcademyZonderModulesPage(): JSX.Element {
  return (
    <Layout>
      <div className="mx-auto w-full max-w-5xl space-y-10 px-5 py-8">
        <h1 className="text-[22px] font-bold text-ink-900">Academy</h1>
        <WebinarsSection />
        <HupperColleges />
      </div>
    </Layout>
  );
}

export function ELearningsPage(): JSX.Element {
  const [view, setView] = useState<View>({ name: 'overview' });

  return (
    <Layout>
      {view.name === 'overview' && (
        <div className="mx-auto w-full max-w-5xl space-y-10 px-5 py-8">
          <h1 className="text-[22px] font-bold text-ink-900">Academy</h1>
          <StudentCourseOverview
            onOpenCourse={(courseId, directLessonId) =>
              setView(
                directLessonId
                  ? { name: 'lesson', courseId, lessonId: directLessonId, direct: true }
                  : { name: 'course', courseId },
              )
            }
          />
          <WebinarsSection />
          <HupperColleges />
        </div>
      )}

      {view.name === 'course' && (
        <StudentCourseDetail
          courseId={view.courseId}
          onBack={() => setView({ name: 'overview' })}
          onOpenLesson={(lessonId) =>
            setView({ name: 'lesson', courseId: view.courseId, lessonId })
          }
          onOpenPractice={(moduleId) =>
            setView({ name: 'practice', courseId: view.courseId, moduleId })
          }
          onStartExam={() => setView({ name: 'exam', courseId: view.courseId })}
        />
      )}

      {view.name === 'lesson' && (
        <StudentLessonViewer
          courseId={view.courseId}
          lessonId={view.lessonId}
          onNavigateToLesson={(lessonId) =>
            setView({ name: 'lesson', courseId: view.courseId, lessonId })
          }
          onStartPractice={(moduleId) =>
            setView({ name: 'practice', courseId: view.courseId, moduleId })
          }
          direct={view.direct ?? false}
          onBack={() =>
            setView(
              view.direct ? { name: 'overview' } : { name: 'course', courseId: view.courseId },
            )
          }
        />
      )}

      {view.name === 'exam' && (
        <StudentExam
          courseId={view.courseId}
          onBack={() => setView({ name: 'course', courseId: view.courseId })}
        />
      )}

      {view.name === 'practice' && (
        <StudentPractice
          courseId={view.courseId}
          moduleId={view.moduleId}
          onBack={() => setView({ name: 'course', courseId: view.courseId })}
        />
      )}
    </Layout>
  );
}
